import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import OpSelect from 'op2mise-react-widgets/OpSelect/OpSelect.jsx';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner.jsx';
import Switch from 'shared/components/switch/Switch';
import { OpTextInput } from 'op2mise-react-widgets';
import Button from 'shared/components/button/Button';
import { validateBreakChannelType } from 'components/break-patterns/breakPattern.helpers.js';
import BreakPatternErrorMessage from 'components/break-patterns/components/BreakPatternErrorMessage.jsx';
import { GetBreakChannelTypeFormOptionsAPI } from 'api/ChannelAPI.js';
import useStore from 'store/AccountStore.js';
import CustomBusinessRulesService from 'components/business-rules-custom/CustomBusinessRulseServices.js';
import { Constants, FORM_TYPE } from '../../constants.js';
import Styles from './breakTypeEditor.module.css';

function FormField({ children, className, error, label }) {
  return (
    <div>
      <div className={`${Styles.formField} ${className}`}>
        {label && <span className={Styles.formFieldLabel}>{label}</span>}
        <div className={Styles.formFieldControl}>{children}</div>
      </div>

      <div className={Styles.formFieldError}>
        <BreakPatternErrorMessage message={error} />
      </div>
    </div>
  );
}

function BreakTypeEditor({ channelInfo, data, mode, onClose, onSave }) {
  const { user } = useStore((state) => state);

  const [parameters, setParameters] = useState(() => data ?? Constants.defaultBreakPatternData);
  const [breakPatternFormOptions, setBreakPatternFormOptions] = useState({
    contentTypes: [],
    events: [],
    segments: [],
    selection: [],
  });
  const [isFetchingFormOptions, setIsFetchingFormOptions] = useState(true);
  const [formErrors, setFormErrors] = useState({
    type: null,
    slotPosition: null,
    position: null,
    selection: null,
    externalID: null,
    event: null,
    priority: null,
    hasFormErrors: false,
  });

  const handleSelectChange = (field, event) => {
    const value = event?.target?.itemData?.name ?? null;

    setParameters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInputChange = (field, event) => {
    const value = event?.target?.value ?? null;

    setParameters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field) => {
    setParameters((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSaveForm = () => {
    const validationResults = validateBreakChannelType(parameters);

    if (validationResults.hasErrors) {
      setFormErrors((prev) => ({
        ...prev,
        ...validationResults,
        hasFormErrors: true,
      }));
      return;
    }

    setFormErrors(Constants.defaultBreakChannelTypeFormErrors);
    onSave(parameters, mode);
    onClose();
  };

  const parseRuleValue = (value) => {
    if (!value || typeof value !== 'string') return [];

    return value
      .split(',')
      .map((v) => ({ name: v.trim() }))
      .filter(Boolean);
  };

  const fetchOrganisationBusinessRules = async () => {
    try {
      const rules =
        (await CustomBusinessRulesService.getAllBusinessRulesOrganisation(user.organisationId)) ??
        [];

      let segments = [];
      let selection = [];

      rules.data.forEach((rule) => {
        if (rule.name === 'Break Pattern Segments') {
          segments = parseRuleValue(rule.value);
        }

        if (rule.name === 'Break Pattern Selection') {
          selection = parseRuleValue(rule.value);
        }
      });

      setBreakPatternFormOptions((prev) => ({
        ...prev,
        segments,
        selection,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChannelTemplateFormOptions = () => {
    // Capture saved values before the async call so the closures below stay stable.
    const savedType = parameters.type;
    const savedEventName = parameters.eventName;

    // WORKAROUND: Syncfusion DropDownList cannot display a value that is absent from
    // its dataSource. When the API returns options that no longer include a previously
    // saved type/eventName (e.g. the option was deleted remotely), or when the API
    // fails entirely, we inject the saved value as a synthetic entry so the dropdown
    // can still render it.
    // FIXME: Replace this once we adopt ComboBox or implement a
    // proper stale-value strategy.
    const injectIfMissing = (list, value) => {
      if (value && !list.some((item) => item.name === value)) {
        return [...list, { name: value }];
      }
      return list;
    };

    const applyWorkaround = (contentTypes, events) => ({
      contentTypes: injectIfMissing(contentTypes, savedType),
      events: injectIfMissing(events, savedEventName),
    });

    GetBreakChannelTypeFormOptionsAPI({
      channelId: channelInfo.channelId,
      onSuccess: (res) => {
        const contentTypes = (res.contentTypes ?? []).map((type) => ({ name: type }));
        const events = (res.events ?? []).map((event) => ({ name: event }));
        setBreakPatternFormOptions((prevState) => ({
          ...prevState,
          ...applyWorkaround(contentTypes, events),
        }));
      },
      onError: () => {
        setBreakPatternFormOptions((prevState) => ({
          ...prevState,
          ...applyWorkaround([], []),
        }));
      },
    });
  };

  const buildFormOptions = async () => {
    setIsFetchingFormOptions(true);

    await fetchOrganisationBusinessRules();
    fetchChannelTemplateFormOptions();

    setIsFetchingFormOptions(false);
  };

  const formFieldConfigs = [
    {
      key: 'type',
      label: 'Type',
      component: 'select',
      dataSource: breakPatternFormOptions.contentTypes,
      filterBarPlaceholder: 'Search',
      placeholder: 'Please select a type',
    },
    {
      key: 'slotPosition',
      label: 'Segment*',
      component: 'select',
      dataSource: breakPatternFormOptions.segments,
      filterBarPlaceholder: 'Search Segment',
      placeholder: 'Please select a segment',
    },
    {
      key: 'position',
      label: 'Position',
      component: 'input',
      inputType: 'number',
    },
    {
      key: 'selection',
      label: 'Selection*',
      component: 'select',
      dataSource: breakPatternFormOptions.selection,
      filterBarPlaceholder: 'Search Selection',
      placeholder: 'Please select a selection',
    },
    {
      key: 'externalID',
      label: 'External ID',
      component: 'input',
      inputType: 'text',
    },
    {
      key: 'eventName',
      label: 'Event',
      component: 'select',
      dataSource: breakPatternFormOptions.events,
      filterBarPlaceholder: 'Search',
      placeholder: 'Please select an event',
    },
    {
      key: 'priority',
      label: 'Priority',
      component: 'input',
      inputType: 'number',
    },
    {
      key: 'optional',
      label: 'Optional',
      render: ({ key, label }) => (
        <FormField key={key} label={label} className={Styles.formField}>
          <Switch
            text={['No', 'Yes']}
            active={parameters.optional}
            onSwitch={() => handleSwitchChange('optional')}
            blurredOnInactive={false}
          />
        </FormField>
      ),
    },
  ];

  const renderField = (config) => {
    if (config.render) return config.render(config);
    if (config.component === 'select') {
      return (
        <FormField key={config.key} label={config.label} error={formErrors[config.key]}>
          <OpSelect
            allowFiltering
            containerClass={Styles.selectContainer}
            dataSource={config.dataSource}
            fields={{ text: 'name', value: 'name' }}
            filterBarPlaceholder={config.filterBarPlaceholder}
            // label=" "
            onChange={(value) => handleSelectChange(config.key, value)}
            placeholder={config.placeholder}
            value={parameters[config.key]}
          />
        </FormField>
      );
    }
    return (
      <FormField key={config.key} label={config.label} error={formErrors[config.key]}>
        <OpTextInput
          containerClass={Styles.textInput}
          onChange={(event) => handleInputChange(config.key, event)}
          type={config.inputType}
          value={parameters[config.key]}
        />
      </FormField>
    );
  };

  useEffect(() => {
    buildFormOptions();
  }, []);

  return (
    <Modal show enforceFocus={false} centered className={Styles.breakTypeEditorModal}>
      <Modal.Body className={Styles.modalBody}>
        <div className={Styles.modalHeaderContainer}>
          <h4>{mode === FORM_TYPE.EDIT ? 'Modify Row' : 'Add Row'}</h4>
        </div>

        {isFetchingFormOptions ? (
          <div>
            <BootstrapSpinner pushTop="-140px" />
          </div>
        ) : (
          <>
            <div className={Styles.modalFormContainer}>{formFieldConfigs.map(renderField)}</div>

            <hr />

            <div className={Styles.modalFooter}>
              <Button onClick={onClose} text="Cancel" defaultBtn />
              <Button onClick={handleSaveForm} text={mode === FORM_TYPE.EDIT ? 'Save' : 'Add'} />
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default BreakTypeEditor;
